import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PackageCheck, TriangleAlert } from 'lucide-react';

import { api } from '@/lib/api';
import { isAppUpdateAvailable, type AppUpdateCheckDto, type DeviceDto } from '@gc/shared';

/**
 * Which gaming PCs are running the current kiosk package.
 *
 * A published release reaches each PC on its own: the machine checks every half
 * hour, downloads in the background, and installs at its next logon — never
 * mid-session. That is the right behaviour and it is also invisible, so this
 * chip is the answer to the only question it leaves open: HAS it reached them.
 *
 * The version comes from `agent.hello`, i.e. from the machine itself, not from
 * anything the server assumed. A PC that never checks in never turns green.
 *
 * Renders NOTHING when the venue has published no package, or when no PC has
 * ever reported a version — there is nothing to be behind. Everything current
 * is a quiet neutral chip carrying the version; anything behind turns amber and
 * names the machines. This is the header of the busiest screen in the building,
 * so only the amber state is allowed to draw the eye.
 */
export function PortalAppVersion({ devices }: { devices: readonly DeviceDto[] }) {
  const { t } = useTranslation();
  const [published, setPublished] = useState<string | null>(null);

  useEffect(() => {
    const abort = new AbortController();
    api
      .get<AppUpdateCheckDto>('/app-releases/current', abort.signal)
      .then((result) => setPublished(result.version))
      .catch(() => {
        // Silent by design. This is a fleet-maintenance nicety on a screen
        // cashiers use to take money; an older API, or a deployment that never
        // publishes, must leave the Devices header exactly as it was.
      });
    return () => abort.abort();
  }, []);

  if (!published) return null;

  // Only PCs that have actually reported in. A device that has never run an
  // agent has nothing to be out of date about, and counting it would leave the
  // warning permanently on for a tile nobody has set up yet.
  const reporting = devices.filter((device) => device.agentVersion !== null);
  if (reporting.length === 0) return null;

  const behind = reporting.filter((device) => isAppUpdateAvailable(device.agentVersion, published));

  if (behind.length === 0) {
    return (
      <span
        className="flex items-center gap-1.5 rounded-full border border-subtle px-3 py-1 text-xs text-content-secondary"
        title={t('devices.portalApp.allCurrentTitle', { version: published })}
      >
        <PackageCheck className="h-3.5 w-3.5" aria-hidden />
        <span className="tabular">{published}</span>
      </span>
    );
  }

  return (
    <span
      className="flex items-center gap-1.5 rounded-full border border-ending/40 bg-ending/10 px-3 py-1 text-xs text-ending"
      // The names, not just the count: the next question after "how many" is
      // always "which ones", and a tooltip answers it without another screen.
      title={t('devices.portalApp.behindTitle', {
        version: published,
        names: behind.map((device) => `${device.name} (${device.agentVersion})`).join(', '),
      })}
    >
      <TriangleAlert className="h-3.5 w-3.5" aria-hidden />
      {t('devices.portalApp.behind', { count: behind.length })}
    </span>
  );
}
